import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, X } from 'lucide-react';

/* LE MESSAGE GLOBAL, en haut au centre (App le monte une fois).
   Une ERREUR reste dix secondes, et le temps s'arrête tant que la souris est
   dessus (2026-10-01) : en 3,5 secondes on ne lisait pas un message de deux
   lignes, encore moins le temps d'atteindre le bouton. Elle porte un bouton
   « Copier » qui met dans le presse-papiers le message ET sa source (requête,
   statut, écran, client, exercice, heure), de quoi la signaler sans recopier
   une capture. Une confirmation garde ses 3,5 secondes et n'a pas de bouton.
   Une erreur se FERME aussi d'un clic (✕, 2026-10-09) : elle reste longtemps
   pour qu'on la lise, pas pour qu'on l'attende.
   Une INFORMATION (un refus normal : version verrouillée, mois clos) a la
   même tenue, Copier et ✕ compris, sur un fond ambre clair : ce n'est pas une
   panne, l'application fait ce qu'on attend d'elle. */
const DURATION = { ok: 3500, error: 10000 };

/** Le texte copié : le message, puis d'où il vient, une ligne par fait connu. */
export function errorReport({ msg, source, context, at }, t) {
  const lines = [`${t('common:toast.report.message')} : ${msg}`];
  if (source?.request) lines.push(`${t('common:toast.report.request')} : ${source.request}`);
  if (source?.status != null) lines.push(`${t('common:toast.report.status')} : ${source.status}`);
  if (context?.screen) lines.push(`${t('common:toast.report.screen')} : ${context.screen}`);
  if (context?.tenant) lines.push(`${t('common:toast.report.tenant')} : ${context.tenant}`);
  if (context?.year) lines.push(`${t('common:toast.report.year')} : ${context.year}`);
  if (at) lines.push(`${t('common:toast.report.date')} : ${new Date(at).toLocaleString('de-CH')}`);
  return lines.join('\n');
}

export default function Toast({ toast, onDone }) {
  const { t } = useTranslation();
  // Erreur ou information : même durée, mêmes boutons, couleur différente.
  const isError = toast.type === 'error' || toast.type === 'info';
  const [hover, setHover] = useState(false);
  const [copied, setCopied] = useState(false);
  /* App recrée `onDone` à chaque rendu : le lire par une référence évite de
     relancer le minuteur à chaque rendu de l'application. */
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    if (hover) return undefined;
    const id = setTimeout(() => done.current(), isError ? DURATION.error : DURATION.ok);
    return () => clearTimeout(id);
  }, [hover, isError]);

  const copy = () => {
    const text = errorReport(toast, t);
    Promise.resolve(navigator.clipboard?.writeText(text))
      .then(() => setCopied(true))
      .catch(() => {});
  };

  return (
    <div className={`toast${toast.type === 'error' ? ' toast--error' : ''}${toast.type === 'info' ? ' toast--info' : ''}`}
         role={isError ? 'alert' : 'status'}
         onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <span className="toast__msg">{toast.msg}</span>
      {isError && (
        <button type="button" className="toast__copy" onClick={copy}>
          {copied ? t('common:toast.copied') : t('common:toast.copy')}
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
        </button>
      )}
      {isError && (
        <button type="button" className="toast__close" onClick={() => done.current()}
                title={t('common:actions.close')} aria-label={t('common:actions.close')}>
          <X size={15} strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  );
}
